import './assets/styles/bootstrap-lite.css' // 2026-10-03：取代 CDN 的 bootstrap.min.css（必須在所有站內樣式之前）
import { resolveInitialLocale } from './runtime/geo-locale'
import { createApp, defineAsyncComponent, nextTick } from 'vue'
import { createPinia } from 'pinia'
import { createHead } from '@unhead/vue/client'
import App from './App.vue'
import router from './router'
import i18n from './i18n'
// 2026-10-03：FontAwesome 整套移除，圖示改 src/components/ui/Icon.vue 內嵌 SVG
import '@/assets/styles/motion.css'
import RailLayout from './components/layout/RailLayout.vue'
import OutlineNav from './components/ui/OutlineNav.vue'
import TypeBadge from './components/ui/TypeBadge.vue'
import CopyButton from './components/ui/CopyButton.vue'
import { useMainStore } from './store'
import { installViewTransitions, markAppMounted } from './runtime/view-transition'
import { installPrefetch } from './runtime/prefetch'
import { installImageReveal } from './runtime/image-reveal'
import { installWorkMorph } from './runtime/work-morph'
import { seatIntro } from './runtime/intro-seat.mjs'
import { installPreloadReload } from './runtime/preload-reload.mjs'

const app = createApp(App)
const pinia = createPinia()
const head = createHead()

// 換頁銜接（View Transitions）必須是最後一個 beforeResolve：前面的守衛（內文預載、管理介面）都等完才拍舊畫面
installViewTransitions(router)

// 部署之後開著舊分頁換頁會抓不到舊切塊（vite:preloadError）：重載一次、有保護不無限重載 → src/runtime/preload-reload.mjs
installPreloadReload({ router })

app.use(pinia)
app.use(router)
app.use(head)
app.use(i18n)
const initialLocaleReady = router.isReady().then(() => resolveInitialLocale(router))

// Keep legacy interactive components available without shipping their runtimes to every page.
const withCodeEditor = loader => defineAsyncComponent(() => Promise.all([
  loader(),
  import('codemirror/mode/javascript/javascript.js'),
  import('codemirror/lib/codemirror.css'),
]).then(([module]) => module.default))
app.component('p5-vue-mirror', withCodeEditor(() => import('./components/p5-vue-mirror.vue')))
app.component('CodeArea', withCodeEditor(() => import('./components/CodeArea.vue')))
app.component('RailLayout', RailLayout)
app.component('OutlineNav', OutlineNav)
app.component('TypeBadge', TypeBadge)
app.component('CopyButton', CopyButton)

// Global mixin → provide/inject or global properties
app.config.globalProperties.$cssbg = function(url) {
  let use_url = (url && url !== '') ? url : '/img/default.jpg'
  return {
    'background-image': `url("${use_url}")`
  }
}

// Analytics: one installation and one page_view per successful pathname transition.
// Stream 5436123046 was verified 2026-09-09 with pageChangesEnabled=false.
// Do not enable its automatic history pageviews alongside this route tracker.
// Known junk, found in the 2026-10-18 GA4 audit: trafficheap.cc referral spam (one-day burst on 9/26)
// and a crawler reporting a 1280x1200 screen (Singapore Windows Chrome, ~0% engagement, ~20% of 90-day users).
// The page still renders for them; they are just never sent to GA.
function isAnalyticsJunk () {
  try {
    if (/(^|\.)trafficheap\.cc$/i.test(new URL(document.referrer).hostname)) return true
  } catch {}
  return typeof screen !== 'undefined' && screen.width === 1280 && screen.height === 1200
}
if (typeof window !== 'undefined' && import.meta.env.PROD &&
    !(typeof navigator !== 'undefined' && navigator.webdriver === true) &&
    ['cheyuwu.com', 'www.cheyuwu.com'].includes(window.location.hostname) &&
    !isAnalyticsJunk() &&
    !window.__cheyuAnalyticsInitialized) {
  window.__cheyuAnalyticsInitialized = true
  const measurementId = 'G-MZE0KS2KLX'
  window.dataLayer = window.dataLayer || []
  window.gtag = function () { window.dataLayer.push(arguments) }
  window.gtag('js', new Date())
  const withoutQuery = value => {
    try { const url = new URL(value); return url.origin + url.pathname } catch { return '' }
  }
  const normalizePath = path => path.endsWith('/') || path.split('/').pop().includes('.') ? path : path + '/'
  const pageLocationFor = path => {
    const url = new URL(window.location.origin + normalizePath(path))
    const query = new URLSearchParams(window.location.search)
    for (const [key, value] of query) {
      if (['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'utm_id', 'gclid'].includes(key)) url.searchParams.append(key, value)
    }
    return url.href
  }
  let internalTraffic = false
  try {
    const internal = new URLSearchParams(window.location.search).get('internal')
    if (internal === '1') localStorage.setItem('cheyu_internal', '1')
    if (internal === '0') localStorage.removeItem('cheyu_internal')
    internalTraffic = localStorage.getItem('cheyu_internal') === '1'
  } catch {}
  const trafficParameters = internalTraffic ? { traffic_type: 'internal' } : {}
  const initialLocation = pageLocationFor(new URL(window.location.href).pathname)
  const initialReferrer = withoutQuery(document.referrer)
  window.gtag('set', { page_location: initialLocation, page_referrer: initialReferrer })
  window.gtag('config', measurementId, {
    send_page_view: false, page_location: initialLocation, page_referrer: initialReferrer,
    ...trafficParameters,
  })
  let previousPath, previousLocation
  const trackPage = async (to, from, failure) => {
    if (failure) return
    const pageLocation = pageLocationFor(to.path)
    const pageReferrer = previousLocation || withoutQuery(document.referrer)
    const locationParameters = { page_location: pageLocation, page_referrer: pageReferrer }
    window.gtag('set', locationParameters)
    // Config has higher precedence than global set. Update both without a pageview.
    window.gtag('config', measurementId, { ...locationParameters, ...trafficParameters, update: true, send_page_view: false })
    if (to.path === previousPath) return
    previousPath = to.path
    previousLocation = withoutQuery(pageLocation)
    await nextTick()
    // unhead 把 <title> 寫進 DOM 的動作排在 setTimeout(0)（@unhead/vue client）；再讓出一拍，document.title 才是新頁的字串
    if (typeof setTimeout === 'function') await new Promise(resolve => setTimeout(resolve, 0))
    window.gtag('event', 'page_view', {
      send_to: measurementId,
      ...trafficParameters,
      page_location: pageLocation,
      // 完整 <title>（含作者後綴），GA4 報表與 Google 顯示同一個字串
      page_title: document.title,
      page_referrer: pageReferrer,
    })
  }
  router.isReady().then(async () => {
    await initialLocaleReady
    router.afterEach(trackPage)
    trackPage(router.currentRoute.value)
  })
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://www.googletagmanager.com/gtag/js?id=' + measurementId
  document.head.appendChild(script)
}

// 只在瀏覽器跑的初始化
function initClientOnlyFeatures() {
  const store = useMainStore()

  // 2026-10-03：/manage 後台與 Firebase 退役（資料收進 repo，見 archive/firebase-2026-10-03/）
  // 2026-10-03：移除每頁都抓 /static/sketchs.json（223 KB）的 fetch——寫進 store.userData 後全站沒有任何元件讀

  setTimeout(() => {
    store.setLoading(false)
  }, 100)
}

// Only mount the app in browser environment
if (typeof window !== 'undefined') {
  router.isReady().then(async () => {
    await initialLocaleReady
    // 預載要在掛載前裝好：第一頁掛載時就開始觀察畫面裡的站內連結
    installPrefetch(router)
    app.mount('#app')
    markAppMounted()
    // 開場標誌入座頁首（一個分頁一次；減少動態、爬蟲不顯示；預渲染不動它）
    seatIntro()
    installImageReveal()
    installWorkMorph()
    
    // Set initial locale based on current route
    const initialPath = window.location.pathname
    const initialLocale = (initialPath === '/zh' || initialPath.startsWith('/zh/')) ? 'zh' : 'en'
    i18n.global.locale.value = initialLocale

    
    // Trigger the render-event for Puppeteer
    document.dispatchEvent(new Event('render-event'))
    // Delay the initialization to ensure pre-rendering has completed
    setTimeout(() => {
      initClientOnlyFeatures()
    }, 20)
  })
}

export { app, router }
